import React, { useEffect, useState } from "react";
import { LineChart, Line, XAxis, Tooltip, CartesianGrid } from "recharts";
import axios from "axios";

import { CloudArrowDownIcon, FunnelIcon } from "@heroicons/react/24/outline";
import ReportTable from './ReportTable';

const Report = () => {
  const [summary, setSummary] = useState({
    live_calls: 0,
    total_targets: 0,
    active_targets: 0,
    inactive_targets: 0,
    total_campaigns: 0,
    active_campaigns: 0,
    inactive_campaigns: 0
  });

  const [graphData, setGraphData] = useState([
    { time: "03:00 AM", answered: 0, missed: 0, failed: 0 },
    { time: "06:00 AM", answered: 0, missed: 0, failed: 0 },
    { time: "09:00 AM", answered: 0, missed: 0, failed: 0 },
    { time: "12:00 PM", answered: 0, missed: 0, failed: 0 },
    { time: "03:00 PM", answered: 0, missed: 0, failed: 0 },
    { time: "06:00 PM", answered: 0, missed: 0, failed: 0 },
    { time: "09:00 PM", answered: 0, missed: 0, failed: 0 },
  ]);

  // Add new state for total counts
  const [totalCounts, setTotalCounts] = useState({
    answered: 0,
    missed: 0,
    failed: 0
  });

  const fetchCallData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // First get the user profile to get userId
      const profileResponse = await axios.get('https://didrouteapi.com/api/users/profile', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      const userId = profileResponse.data.id;
      
      // Get live calls from dialogs endpoint with userId
      const dialogResponse = await axios.get(`https://didrouteapi.com/api/dialogs`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          userId: userId,
          page: 1,
          limit: 100,
        },
      });

      // Update live calls count from dialogs data
      const liveCalls = dialogResponse.data.data.dialogs?.length || 0;

      setSummary(prev => ({
        ...prev,
        live_calls: liveCalls
      }));

      // Rest of the existing fetchCallData logic for graph data
      const response = await axios.get('https://didrouteapi.com/api/acc', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          page: 1,
          limit: 100,
        },
      });

      // Initialize counters for each time slot
      const timeSlots = {
        "03:00 AM": { answered: 0, missed: 0, failed: 0 },
        "06:00 AM": { answered: 0, missed: 0, failed: 0 },
        "09:00 AM": { answered: 0, missed: 0, failed: 0 },
        "12:00 PM": { answered: 0, missed: 0, failed: 0 },
        "03:00 PM": { answered: 0, missed: 0, failed: 0 },
        "06:00 PM": { answered: 0, missed: 0, failed: 0 },
        "09:00 PM": { answered: 0, missed: 0, failed: 0 },
      };

      // Calculate total counts while processing records
      const totals = {
        answered: 0,
        missed: 0,
        failed: 0
      };

      // Process each call record
      response.data.data.records.forEach(record => {
        const callTime = new Date(record.time);
        const hours = callTime.getHours();
        
        // Determine which time slot this call belongs to
        let timeSlot;
        if (hours >= 0 && hours < 3) timeSlot = "03:00 AM";
        else if (hours >= 3 && hours < 6) timeSlot = "06:00 AM";
        else if (hours >= 6 && hours < 9) timeSlot = "09:00 AM";
        else if (hours >= 9 && hours < 12) timeSlot = "12:00 PM";
        else if (hours >= 12 && hours < 15) timeSlot = "03:00 PM";
        else if (hours >= 15 && hours < 18) timeSlot = "06:00 PM";
        else if (hours >= 18 && hours < 21) timeSlot = "09:00 PM";
        else timeSlot = "03:00 AM"; // For hours 21-24

        // Increment the appropriate counter based on SIP code
        if (record.sip_code === '200') {
          timeSlots[timeSlot].answered++;
          totals.answered++;
        } else if (record.sip_code === '487') {
          timeSlots[timeSlot].missed++;
          totals.missed++;
        } else if (record.sip_code) {
          timeSlots[timeSlot].failed++;
          totals.failed++;
        }
      });

      // Convert the processed data into the format needed for the graph
      const newGraphData = Object.entries(timeSlots).map(([time, counts]) => ({
        time,
        ...counts
      }));

      setGraphData(newGraphData);
      setTotalCounts(totals);
    } catch (error) {
      console.error('Error fetching call data:', error);
    }
  };

  const fetchSummary = async () => {
    try {
      // Get the token from localStorage
      const token = localStorage.getItem('token');
      
      const response = await axios.get('https://didrouteapi.com/api/reports/summary', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setSummary(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching report summary:', error);
    }
  };

  // Add an interval to update live calls regularly
  useEffect(() => {
    fetchCallData();
    fetchSummary();
    
    // Update live calls every 30 seconds
    const interval = setInterval(() => {
      fetchCallData();
    }, 30000);

    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="bg-white text-black p-3 sm:p-6 rounded-xl shadow-2xl space-y-2 ">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
        <div>
          <h2 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
            CDR Report
          </h2>
          <p className="text-gray-600 text-sm mt-1">
            Real-time call analytics dashboard
          </p>
        </div>
        {/* <div className="flex space-x-3">
          <button className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-200 font-medium shadow-lg shadow-blue-500/20">
            Day
          </button>
          <button className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all duration-200 font-medium">
            Week
          </button>
        </div> */}
      </div>

      {/* Chart Section */}
      <div className="flex flex-col lg:flex-row gap-8 ">
        <div className="flex-1 min-w-0 rounded-xl p-4 overflow-x-auto border border-gray-400">
          <div className="w-full" style={{ minWidth: "300px" }}>
            <LineChart
              width={600}
              height={300}
              data={graphData}
              className="mx-auto"
              style={{ width: "100%", height: "auto" }}
              responsive={true}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="time" stroke="#9CA3AF" tick={{ fontSize: 10 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid rgb(59, 130, 246)",
                  borderRadius: "8px",
                  padding: "12px",
                }}
              />
              <Line
                type="monotone"
                dataKey="answered"
                stroke="#10B981"
                strokeWidth={3}
                dot={{ fill: "#10B981", strokeWidth: 2 }}
              />
              <Line
                type="monotone"
                dataKey="missed"
                stroke="#F59E0B"
                strokeWidth={3}
                dot={{ fill: "#F59E0B", strokeWidth: 2 }}
              />
              <Line
                type="monotone"
                dataKey="failed"
                stroke="#EF4444"
                strokeWidth={3}
                dot={{ fill: "#EF4444", strokeWidth: 2 }}
              />
            </LineChart>
          </div>
          <div className="flex flex-wrap justify-center mt-2 gap-6">
            <div className="flex items-center space-x-2">
              <span className="w-3 h-3 rounded-full bg-green-500 shadow-lg shadow-green-500/20"></span>
              <p className="text-xs font-medium">
                Answered Calls: {totalCounts.answered}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <span className="w-3 h-3 rounded-full bg-yellow-500 shadow-lg shadow-yellow-500/20"></span>
              <p className="text-xs font-medium">
                Missed Calls: {totalCounts.missed}
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <span className="w-3 h-3 rounded-full bg-red-500 shadow-lg shadow-red-500/20"></span>
              <p className="text-xs font-medium">
                Failed Calls: {totalCounts.failed}
              </p>
            </div>

          </div>
        </div>

        {/* Report Summary Section - Updated width */}
        <div className="flex flex-col w-full lg:w-64 bg-white border border-gray-400 p-2 rounded-xl">
          <h3 className="text-lg font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
            Report Summary
          </h3>

          <div className="space-y-2">
            <div className="flex justify-between items-center p-2 rounded-lg bg-gray-300/40">
              <p className="font-medium text-xs">Live Calls</p>
              <p className="text-green-400 font-semibold text-md">
                {summary.live_calls}
              </p>

            </div>
            <div className="flex justify-between items-center p-2 rounded-lg bg-gray-300/40">
              <p className="font-medium text-xs">Total Targets</p>
              <p className="text-yellow-400 font-semibold text-md">
                {summary.total_targets}
              </p>


            </div>

            <div className="flex justify-between items-center p-2 rounded-lg bg-gray-300/40">
              <p className="font-medium text-xs">Active Targets</p>
              <p className="text-green-400 font-semibold text-md">
                {summary.active_targets}

              </p>

            </div>

            <div className="flex justify-between items-center p-2 rounded-lg bg-gray-300/40">
              <p className="font-medium text-xs">Inactive Targets</p>
              <p className="text-red-400 font-semibold text-md">
                {summary.inactive_targets}


              </p>
            </div>

            <div className="flex justify-between items-center p-2 rounded-lg bg-gray-300/40">
              <p className="font-medium text-xs">Total Campaigns</p>
              <p className="text-yellow-400 font-semibold text-md">
                {summary.total_campaigns}



              </p>
            </div>

            <div className="flex justify-between items-center p-2 rounded-lg bg-gray-300/40">
              <p className="font-medium text-xs">Active Campaigns</p>
              <p className="text-green-400 font-semibold text-md">



                {summary.active_campaigns}
              </p>
            </div>

            <div className="flex justify-between items-center p-2 rounded-lg bg-gray-300/40">
              <p className="font-medium text-xs">Inactive Campaigns</p>
              <p className="text-red-400 font-semibold text-md">



                {summary.inactive_campaigns}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Add Report Table Section */}
      <div className="mt-4">
        <ReportTable />
      </div>

      {/* Footer Section */}
      <div className="flex justify-between items-center mt-8">
        {/* <p className="text-sm text-gray-800 p-2">
          Last updated: {new Date().toLocaleTimeString()}
        </p> */}
        {/* <div className="flex space-x-3">
          <button className="p-2.5 bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-lg shadow-blue-500/20">
            <CloudArrowDownIcon className="h-5 w-5 text-white" />
          </button>
          <button className="p-2.5 bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-lg shadow-blue-500/20">
            <FunnelIcon className="h-5 w-5 text-white" />
          </button>
        </div> */}
      </div>
    </div>
  );
};

export default Report;
